import { useMemo } from 'react';
import { getSubBrandsForBrand, type SubBrandItem } from '@mapara/core';
import { Check, Layers } from 'lucide-react';
import { cn } from '../ui/cn.js';

export interface SubBrandPillsProps {
  /** The currently selected parent brand (e.g. "Caudalie", "SVR", "Bioderma") */
  readonly brand: string | null | undefined;
  /** Normalized keys of sub-brands that the user has unchecked / excluded */
  readonly excludedSubBrands: readonly string[];
  /** Callback fired when the selection changes */
  readonly onChange: (excluded: string[]) => void;
  /** Optional container class name */
  readonly className?: string;
}

/**
 * SubBrandPills component: renders sub-brand / range checkboxes for the selected brand.
 * All sub-brands are checked by default; unticking any pill excludes that sub-brand.
 *
 * Client rule (2026-10-24): Everywhere in the App when we select a brand it also
 * includes its sub-brands automatically unless unticked.
 */
export function SubBrandPills({
  brand,
  excludedSubBrands,
  onChange,
  className,
}: SubBrandPillsProps): React.ReactElement | null {
  const subBrands = useMemo<readonly SubBrandItem[]>(() => {
    if (!brand || brand === 'all' || brand.trim() === '') return [];
    return getSubBrandsForBrand(brand);
  }, [brand]);

  if (subBrands.length === 0) return null;

  const activeCount = subBrands.filter((sb) => !excludedSubBrands.includes(sb.key)).length;
  const allKeys = subBrands.map((sb) => sb.key);

  const toggleSubBrand = (key: string) => {
    if (excludedSubBrands.includes(key)) {
      onChange(excludedSubBrands.filter((k) => k !== key));
    } else {
      onChange([...excludedSubBrands, key]);
    }
  };

  const checkAll = () => {
    onChange([]);
  };

  const uncheckAll = () => {
    onChange([...allKeys]);
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-canvas/60 p-2.5 transition-all space-y-2',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-1.5 font-semibold text-ink">
          <Layers className="h-3.5 w-3.5 text-ink-soft" aria-hidden="true" />
          <span>
            Sous-marques & gammes incluses ({activeCount}/{subBrands.length})
          </span>
          {activeCount < subBrands.length && (
            <span className="rounded bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">
              {subBrands.length - activeCount} exclue(s)
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={checkAll}
            disabled={excludedSubBrands.length === 0}
            className="text-[11px] font-medium text-ink-soft hover:text-ink disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Tout cocher
          </button>
          <span className="text-border" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            onClick={uncheckAll}
            disabled={activeCount === 0}
            className="text-[11px] font-medium text-ink-soft hover:text-ink disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Tout décocher
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {subBrands.map((sb) => {
          const isChecked = !excludedSubBrands.includes(sb.key);
          return (
            <label
              key={sb.key}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold cursor-pointer border select-none transition-all',
                isChecked
                  ? 'border-sky-600 bg-sky-50 text-slate-900 shadow-2xs hover:bg-sky-100'
                  : 'border-border bg-card text-ink-ghost line-through opacity-60 hover:opacity-80',
              )}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleSubBrand(sb.key)}
                className="sr-only"
              />
              <span
                className={cn(
                  'flex h-3.5 w-3.5 items-center justify-center rounded border transition-colors',
                  isChecked
                    ? 'border-sky-600 bg-sky-600 text-white'
                    : 'border-border bg-card text-transparent',
                )}
                aria-hidden="true"
              >
                <Check className="h-2.5 w-2.5 stroke-[3]" />
              </span>
              <span className={isChecked ? 'text-slate-900 font-bold' : 'text-ink-ghost'}>
                {sb.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
